import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  RefreshCw,
  Phone,
  Video,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  MapPin,
} from 'lucide-react';
import { collection, query, where, getDocs, doc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { fetchAccountCalls } from '../../services/departmentCallsService';
import {
  parseFathomSummarySections,
  matchActionItemToTask,
} from '../../utils/fathomSummaryParser';
import MeetingSummaryContent from '../MeetingSummaryContent';
import { generateClientDebrief } from '../../services/openaiService';
import { getPortalEmoji } from '../../utils/portalEmojis';
import { normalizeVisitStatus, parseDateKey, VISIT_TYPES } from '../../services/visitSchedulingService';

const DEPARTMENT_PILL_STYLES = {
  sales: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
  operations: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  technology: 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300',
  finance: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
  marketing: 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300',
  hr: 'bg-pink-100 text-pink-700 dark:bg-pink-900/40 dark:text-pink-300',
};

function departmentPillClass(department) {
  const key = String(department || '').toLowerCase();
  return (
    DEPARTMENT_PILL_STYLES[key] ||
    'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
  );
}

function formatDepartmentLabel(department) {
  const key = String(department || '').toLowerCase();
  if (!key) return null;
  if (key === 'hr') return 'HR';
  return key.charAt(0).toUpperCase() + key.slice(1);
}

function formatCallDate(date) {
  if (!date) return 'No date';
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatRelativeDays(date) {
  if (!date) return '';
  const days = Math.round((Date.now() - date.getTime()) / 86400000);
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 30) return `${days} days ago`;
  const months = Math.round(days / 30);
  return months === 1 ? 'about a month ago' : `${months} months ago`;
}

/** Flatten Fathom markdown into a short plain-text snippet for call cards. */
function markdownToSnippet(markdown, maxChars = 220) {
  const plain = String(markdown || '')
    .replace(/^#{1,6}\s+/gm, '')
    .replace(/^[-*+]\s+/gm, '')
    .replace(/\*\*(.+?)\*\*/g, '$1')
    .replace(/\*(.+?)\*/g, '$1')
    .replace(/\[(.+?)\]\(.+?\)/g, '$1')
    .replace(/\s+/g, ' ')
    .trim();
  if (!plain) return '';
  return plain.length > maxChars ? `${plain.slice(0, maxChars).trimEnd()}…` : plain;
}

function truncateForContext(text, maxChars) {
  const value = String(text || '').trim();
  if (value.length <= maxChars) return value;
  return `${value.slice(0, maxChars)}…`;
}

function actionItemText(item) {
  if (typeof item === 'string') return item.trim();
  return String(item?.description || item?.text || '').trim();
}

function toDate(value) {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value.toDate === 'function') return value.toDate();
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

const checkInStatusStyles = {
  Good: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
  Okay: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300',
  'Not Good': 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
};

/**
 * Client debrief for the CRM Client Overview summary tab: AI-generated rundown of
 * recent calls, check-ins, action items, and what is coming up for the client.
 */
export default function ClientDebrief({ account, clientId, checkIns = [] }) {
  const [calls, setCalls] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [visits, setVisits] = useState([]);
  const [sourcesLoading, setSourcesLoading] = useState(true);
  const [expandedCallId, setExpandedCallId] = useState(null);
  const [showAllCalls, setShowAllCalls] = useState(false);
  const [debrief, setDebrief] = useState(account?.clientDebrief || null);
  const [generating, setGenerating] = useState(false);
  const [generateError, setGenerateError] = useState(null);

  useEffect(() => {
    setDebrief(account?.clientDebrief || null);
    // Only reset when switching clients — local state wins after a fresh generation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  useEffect(() => {
    if (!clientId) return;
    let cancelled = false;

    const load = async () => {
      setSourcesLoading(true);
      const email = String(account?.contact?.email || account?.email || '').trim();
      const emailVariants = [...new Set([email, email.toLowerCase()].filter(Boolean))];

      const [callsResult, tasksResult, visitsResult] = await Promise.allSettled([
        fetchAccountCalls(clientId),
        getDocs(query(collection(db, 'tasks'), where('companyId', '==', clientId))),
        Promise.all(
          Object.values(VISIT_TYPES).flatMap((type) =>
            emailVariants.map(async (em) => {
              const snap = await getDocs(
                query(collection(db, type.requestCollection), where('userEmail', '==', em))
              );
              return snap.docs.map((d) => ({ id: d.id, visitType: type.key, ...d.data() }));
            })
          )
        ),
      ]);

      if (cancelled) return;
      setCalls(callsResult.status === 'fulfilled' ? callsResult.value : []);
      setTasks(
        tasksResult.status === 'fulfilled'
          ? tasksResult.value.docs.map((d) => ({ id: d.id, ...d.data() }))
          : []
      );
      if (visitsResult.status === 'fulfilled') {
        const seen = new Set();
        const merged = [];
        for (const request of visitsResult.value.flat()) {
          const key = `${request.visitType}-${request.id}`;
          if (seen.has(key)) continue;
          seen.add(key);
          merged.push(request);
        }
        setVisits(merged);
      } else {
        setVisits([]);
      }
      setSourcesLoading(false);
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [clientId, account?.contact?.email, account?.email]);

  const completedCalls = useMemo(
    () => calls.filter((c) => c.status === 'Completed' && c.date),
    [calls]
  );

  const visibleCalls = useMemo(
    () => (showAllCalls ? completedCalls.slice(0, 15) : completedCalls.slice(0, 5)),
    [completedCalls, showAllCalls]
  );

  const upcomingCalls = useMemo(() => {
    const cutoff = Date.now() - 86400000;
    return calls
      .filter((c) => c.status === 'Booked' && c.date && c.date.getTime() >= cutoff)
      .sort((a, b) => a.date - b.date)
      .slice(0, 4);
  }, [calls]);

  const upcomingVisits = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return visits
      .map((v) => ({ ...v, status: normalizeVisitStatus(v.status), startDate: parseDateKey(v.day1) }))
      .filter(
        (v) =>
          ['requested', 'scheduled'].includes(v.status) &&
          v.startDate &&
          v.startDate.getTime() >= today.getTime()
      )
      .sort((a, b) => a.startDate - b.startDate);
  }, [visits]);

  const openActionItems = useMemo(() => {
    const items = [];
    const seen = new Set();
    for (const call of completedCalls.slice(0, 10)) {
      for (const item of call.actionItems || []) {
        const text = actionItemText(item);
        if (!text) continue;
        const dedupeKey = text.toLowerCase().slice(0, 200);
        if (seen.has(dedupeKey)) continue;
        seen.add(dedupeKey);
        const task = matchActionItemToTask(item, tasks);
        items.push({
          text,
          assigneeName: typeof item === 'object' ? item?.assigneeName || null : null,
          completed: Boolean(task?.completed || item?.completed),
          callTitle: call.title,
          callDate: call.date,
        });
      }
    }
    const open = items.filter((i) => !i.completed);
    const done = items.filter((i) => i.completed);
    return { open: open.slice(0, 5), doneCount: done.length, totalCount: items.length };
  }, [completedCalls, tasks]);

  const completedCheckIns = useMemo(
    () =>
      (checkIns || []).filter(
        (c) => c.status !== 'pending' && (c.overallStatus || c.overallSatisfaction || c.rating)
      ),
    [checkIns]
  );
  const latestCheckIn = completedCheckIns[0] || null;
  const pendingCheckIn = (checkIns || []).find((c) => c.status === 'pending') || null;

  const recentActivityEvents = useMemo(() => {
    const cutoff = Date.now() - 60 * 86400000;
    const events = [];

    for (const task of tasks) {
      if (!task.completed) continue;
      const date = toDate(task.updatedAt) || toDate(task.createdAt);
      if (!date || date.getTime() < cutoff) continue;
      events.push({
        id: `task-${task.id}`,
        icon: 'task',
        label: task.title,
        detail: 'Task completed',
        department: String(task.department || '').toLowerCase() || null,
        date,
      });
    }
    for (const checkIn of completedCheckIns) {
      const date = toDate(checkIn.submittedAt);
      if (!date || date.getTime() < cutoff) continue;
      events.push({
        id: `checkin-${checkIn.id}`,
        icon: 'checkin',
        label: `${checkIn.month || 'Monthly'} check-in${
          checkIn.overallStatus ? ` — ${checkIn.overallStatus}` : ''
        }`,
        detail: 'Check-in submitted',
        date,
      });
    }
    for (const visit of visits) {
      const date = toDate(visit.updatedAt) || toDate(visit.createdAt);
      if (!date || date.getTime() < cutoff) continue;
      const label = VISIT_TYPES[visit.visitType]?.label || 'Visit';
      events.push({
        id: `visit-${visit.visitType}-${visit.id}`,
        icon: 'visit',
        label,
        detail: `Visit ${normalizeVisitStatus(visit.status)}`,
        date,
      });
    }

    return events.sort((a, b) => b.date - a.date).slice(0, 8);
  }, [tasks, completedCheckIns, visits]);

  const newestSourceDate = useMemo(() => {
    const dates = [
      completedCalls[0]?.date,
      toDate(completedCheckIns[0]?.submittedAt),
      recentActivityEvents[0]?.date,
    ].filter(Boolean);
    return dates.length ? new Date(Math.max(...dates.map((d) => d.getTime()))) : null;
  }, [completedCalls, completedCheckIns, recentActivityEvents]);

  const generatedAtDate = toDate(debrief?.generatedAt);
  const debriefIsStale = Boolean(
    generatedAtDate && newestSourceDate && newestSourceDate.getTime() > generatedAtDate.getTime()
  );
  const hasSourceData = completedCalls.length > 0 || completedCheckIns.length > 0;

  const buildContext = useCallback(() => {
    const sections = [];

    const profileLines = [
      `Company: ${account?.companyName || account?.name || 'Unknown'}`,
      account?.program?.status ? `Program: ${account.program.status}` : null,
      account?.metrics?.revenueGoal
        ? `Revenue goal: $${Number(account.metrics.revenueGoal).toLocaleString()}`
        : null,
      account?.metrics?.currentRevenue
        ? `Current revenue: $${Number(account.metrics.currentRevenue).toLocaleString()}`
        : null,
    ].filter(Boolean);
    sections.push(`=== CLIENT PROFILE ===\n${profileLines.join('\n')}`);

    if (completedCalls.length) {
      const callBlocks = completedCalls.slice(0, 10).map((call) => {
        const parsed = parseFathomSummarySections(call.summary || '');
        const parts = [
          `[${formatCallDate(call.date)}] ${call.title}${
            call.department ? ` (${formatDepartmentLabel(call.department)})` : ''
          }`,
        ];
        if (parsed.keyTakeaways) {
          parts.push(`Key takeaways: ${truncateForContext(markdownToSnippet(parsed.keyTakeaways, 1200), 1200)}`);
        }
        if (parsed.nextSteps) {
          parts.push(`Next steps: ${truncateForContext(markdownToSnippet(parsed.nextSteps, 600), 600)}`);
        }
        if (!parsed.keyTakeaways && !parsed.nextSteps && call.summary) {
          parts.push(`Summary: ${truncateForContext(markdownToSnippet(call.summary, 1200), 1200)}`);
        }
        return parts.join('\n');
      });
      sections.push(`=== RECENT CALLS (newest first) ===\n${callBlocks.join('\n\n')}`);
    }

    if (openActionItems.totalCount) {
      const lines = openActionItems.open.map(
        (item) =>
          `- [open] ${item.text}${item.assigneeName ? ` (${item.assigneeName})` : ''} — from "${item.callTitle}" on ${formatCallDate(item.callDate)}`
      );
      lines.push(`(${openActionItems.doneCount} of ${openActionItems.totalCount} recent action items completed)`);
      sections.push(`=== ACTION ITEMS ===\n${lines.join('\n')}`);
    }

    if (completedCheckIns.length) {
      const lines = completedCheckIns.slice(0, 3).map((checkIn) => {
        const rating = checkIn.overallSatisfaction ?? checkIn.rating;
        const parts = [
          `[${checkIn.month || formatCallDate(toDate(checkIn.submittedAt))}]`,
          checkIn.overallStatus ? `Status: ${checkIn.overallStatus}` : null,
          rating ? `Rating: ${rating}/5` : null,
          checkIn.requestedCheckInCall ? 'Requested a check-in call' : null,
          checkIn.comments || checkIn.additionalFeedback
            ? `Comments: ${truncateForContext(checkIn.comments || checkIn.additionalFeedback, 500)}`
            : null,
        ].filter(Boolean);
        return `- ${parts.join(' | ')}`;
      });
      sections.push(`=== MONTHLY CHECK-INS (newest first) ===\n${lines.join('\n')}`);
    }

    const upcomingLines = [
      ...upcomingCalls.map((call) => `- Call: ${call.title} on ${formatCallDate(call.date)}`),
      ...upcomingVisits.map(
        (visit) =>
          `- ${VISIT_TYPES[visit.visitType]?.label || 'Visit'} (${visit.status}) starting ${formatCallDate(visit.startDate)}`
      ),
      pendingCheckIn ? `- ${pendingCheckIn.month || 'Monthly'} check-in sent, awaiting response` : null,
    ].filter(Boolean);
    if (upcomingLines.length) {
      sections.push(`=== UPCOMING ===\n${upcomingLines.join('\n')}`);
    }

    if (recentActivityEvents.length) {
      const lines = recentActivityEvents.map(
        (event) => `- [${formatCallDate(event.date)}] ${event.label}`
      );
      sections.push(`=== OTHER RECENT ACTIVITY ===\n${lines.join('\n')}`);
    }

    const lastCall = completedCalls[0];
    if (lastCall?.date) {
      sections.push(`Most recent completed call was ${formatRelativeDays(lastCall.date)}.`);
    }

    return sections.join('\n\n');
  }, [
    account,
    completedCalls,
    openActionItems,
    completedCheckIns,
    upcomingCalls,
    upcomingVisits,
    pendingCheckIn,
    recentActivityEvents,
  ]);

  const handleGenerate = async () => {
    if (generating || sourcesLoading || !hasSourceData) return;
    setGenerating(true);
    setGenerateError(null);
    try {
      const generated = await generateClientDebrief({
        companyName: account?.companyName || account?.name || '',
        context: buildContext(),
      });
      const record = {
        ...generated,
        generatedAt: new Date().toISOString(),
        sourceCounts: {
          calls: completedCalls.length,
          checkIns: completedCheckIns.length,
          actionItems: openActionItems.totalCount,
        },
      };
      setDebrief(record);
      try {
        await updateDoc(doc(db, 'accounts', clientId), { clientDebrief: record });
      } catch (persistError) {
        console.warn('Could not persist client debrief:', persistError);
      }
    } catch (error) {
      setGenerateError(error.message || 'Failed to generate the debrief.');
    } finally {
      setGenerating(false);
    }
  };

  const renderCallCard = (call) => {
    const expanded = expandedCallId === call.id;
    const parsed = parseFathomSummarySections(call.summary || '');
    const snippet = markdownToSnippet(parsed.keyTakeaways || call.summary || '');
    const departmentLabel = formatDepartmentLabel(call.department);

    return (
      <div
        key={call.id}
        className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:shadow-sm transition-shadow"
      >
        <button
          type="button"
          className="w-full text-left"
          onClick={() => setExpandedCallId(expanded ? null : call.id)}
        >
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h4 className="font-medium text-gray-900 dark:text-white text-sm">
                  {call.title}
                </h4>
                {departmentLabel && (
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${departmentPillClass(call.department)}`}>
                    {getPortalEmoji(call.department)} {departmentLabel}
                  </span>
                )}
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {formatCallDate(call.date)} · {formatRelativeDays(call.date)}
              </p>
              {!expanded && snippet && (
                <p className="text-sm text-gray-600 dark:text-gray-300 mt-2 line-clamp-2">
                  {snippet}
                </p>
              )}
            </div>
            <span className="text-gray-400 dark:text-gray-500 mt-0.5 shrink-0">
              {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </span>
          </div>
        </button>
        {expanded && (
          <div className="mt-3 space-y-3">
            {parsed.keyTakeaways && (
              <div>
                <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
                  Key Takeaways
                </p>
                <MeetingSummaryContent markdown={parsed.keyTakeaways} />
              </div>
            )}
            {parsed.nextSteps && (
              <div>
                <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
                  Next Steps
                </p>
                <MeetingSummaryContent markdown={parsed.nextSteps} />
              </div>
            )}
            {!parsed.keyTakeaways && !parsed.nextSteps && call.summary && (
              <MeetingSummaryContent markdown={call.summary} />
            )}
            {!call.summary && (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                No summary was recorded for this call.
              </p>
            )}
            {call.recordingUrl && (
              <a
                href={call.recordingUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-sm text-red-600 dark:text-red-400 hover:underline"
              >
                <Video className="w-4 h-4" />
                Watch recording
                <ExternalLink className="w-3 h-3" />
              </a>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* AI Debrief */}
      <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6">
        <div className="flex items-center justify-between gap-3 mb-4">
          <div className="flex items-center gap-2">
            <span className="text-xl">✨</span>
            <h3 className="font-semibold text-gray-900 dark:text-white">Client Debrief</h3>
          </div>
          <div className="flex items-center gap-3">
            {generatedAtDate && (
              <span className="text-xs text-gray-500 dark:text-gray-400">
                Generated {formatRelativeDays(generatedAtDate)}
                {debriefIsStale && (
                  <span className="ml-1 text-amber-600 dark:text-amber-400">
                    · new activity since
                  </span>
                )}
              </span>
            )}
            <button
              type="button"
              onClick={handleGenerate}
              disabled={generating || sourcesLoading || !hasSourceData}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <RefreshCw className={`w-4 h-4 ${generating ? 'animate-spin' : ''}`} />
              {generating ? 'Generating…' : debrief ? 'Refresh' : 'Generate Debrief'}
            </button>
          </div>
        </div>

        {generateError && (
          <p className="text-sm text-red-600 dark:text-red-400 mb-3">{generateError}</p>
        )}

        {!debrief ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {sourcesLoading
              ? 'Loading client activity…'
              : hasSourceData
              ? 'No debrief yet. Generate one to get an AI rundown of this client’s recent calls, check-ins, and what’s next.'
              : 'No call summaries or check-ins yet for this client — the debrief will unlock once there’s activity to analyze.'}
          </p>
        ) : (
          <div className="space-y-5">
            {debrief.overview && (
              <p className="text-sm text-gray-700 dark:text-gray-200 leading-relaxed">
                {debrief.overview}
              </p>
            )}

            {debrief.attention?.length > 0 && (
              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <span>⚠️</span>
                  <p className="text-sm font-semibold text-amber-800 dark:text-amber-300">
                    Needs Attention
                  </p>
                </div>
                <ul className="space-y-1">
                  {debrief.attention.map((item, index) => (
                    <li key={index} className="text-sm text-amber-800 dark:text-amber-200">
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {debrief.recentActivity?.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                    🔎 What They've Been Up To
                  </p>
                  <ul className="space-y-1.5">
                    {debrief.recentActivity.map((item, index) => (
                      <li key={index} className="text-sm text-gray-700 dark:text-gray-300 flex gap-2">
                        <span className="text-gray-400 dark:text-gray-500 shrink-0">•</span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {debrief.focuses?.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                    🎯 Focus Next
                  </p>
                  <ul className="space-y-1.5">
                    {debrief.focuses.map((item, index) => (
                      <li key={index} className="text-sm text-gray-700 dark:text-gray-300 flex gap-2">
                        <span className="text-red-500 shrink-0">→</span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {debrief.departmentPulse?.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                  🧭 Department Pulse
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {debrief.departmentPulse.map((entry, index) => (
                    <div
                      key={index}
                      className="bg-gray-50 dark:bg-gray-800/60 rounded-xl p-3"
                    >
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${departmentPillClass(entry.department)}`}>
                        {getPortalEmoji(entry.department)} {formatDepartmentLabel(entry.department) || entry.department}
                      </span>
                      <p className="text-sm text-gray-700 dark:text-gray-300 mt-2">
                        {entry.note}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
        {/* Recent calls */}
        <div className="lg:col-span-2 bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <span className="text-xl">📞</span>
              <h3 className="font-semibold text-gray-900 dark:text-white">Recent Calls</h3>
            </div>
            {completedCalls.length > 0 && (
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {completedCalls.length} completed
              </span>
            )}
          </div>
          {sourcesLoading ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">Loading calls…</p>
          ) : completedCalls.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              No completed calls with summaries yet.
            </p>
          ) : (
            <div className="space-y-3">
              {visibleCalls.map(renderCallCard)}
              {completedCalls.length > 5 && (
                <button
                  type="button"
                  onClick={() => setShowAllCalls((prev) => !prev)}
                  className="w-full text-center text-sm text-red-600 dark:text-red-400 hover:underline py-1"
                >
                  {showAllCalls
                    ? 'Show fewer'
                    : `Show ${Math.min(completedCalls.length, 15) - 5} more`}
                </button>
              )}
            </div>
          )}
        </div>

        {/* Right rail */}
        <div className="space-y-6">
          {/* Client pulse */}
          <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-xl">💓</span>
              <h3 className="font-semibold text-gray-900 dark:text-white">Client Pulse</h3>
            </div>
            {latestCheckIn ? (
              <div className="space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm text-gray-600 dark:text-gray-300">
                    {latestCheckIn.month || 'Latest check-in'}
                  </span>
                  {latestCheckIn.overallStatus && (
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${checkInStatusStyles[latestCheckIn.overallStatus] || 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'}`}>
                      {latestCheckIn.overallStatus}
                    </span>
                  )}
                </div>
                {(latestCheckIn.overallSatisfaction ?? latestCheckIn.rating) && (
                  <p className="text-sm text-gray-700 dark:text-gray-200">
                    Satisfaction: {latestCheckIn.overallSatisfaction ?? latestCheckIn.rating}/5
                  </p>
                )}
                {(latestCheckIn.comments || latestCheckIn.additionalFeedback) && (
                  <p className="text-sm text-gray-600 dark:text-gray-300 italic">
                    “{truncateForContext(latestCheckIn.comments || latestCheckIn.additionalFeedback, 200)}”
                  </p>
                )}
                {latestCheckIn.requestedCheckInCall && (
                  <p className="text-sm text-amber-600 dark:text-amber-400 font-medium">
                    Requested a check-in call
                  </p>
                )}
              </div>
            ) : (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                No completed check-ins yet.
              </p>
            )}
            {pendingCheckIn && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-3">
                {pendingCheckIn.month || 'Monthly'} check-in sent — awaiting response.
              </p>
            )}
          </div>

          {/* Coming up */}
          <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-xl">📅</span>
              <h3 className="font-semibold text-gray-900 dark:text-white">Coming Up</h3>
            </div>
            {upcomingCalls.length === 0 && upcomingVisits.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Nothing on the calendar yet.
              </p>
            ) : (
              <ul className="space-y-2.5">
                {upcomingCalls.map((call) => (
                  <li key={call.id} className="flex items-start gap-2">
                    <Phone className="w-4 h-4 text-gray-400 dark:text-gray-500 mt-0.5 shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm text-gray-800 dark:text-gray-200 truncate">
                        {call.title}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {formatCallDate(call.date)}
                      </p>
                    </div>
                  </li>
                ))}
                {upcomingVisits.map((visit) => (
                  <li key={`${visit.visitType}-${visit.id}`} className="flex items-start gap-2">
                    <MapPin className="w-4 h-4 text-gray-400 dark:text-gray-500 mt-0.5 shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm text-gray-800 dark:text-gray-200">
                        {VISIT_TYPES[visit.visitType]?.label || 'Visit'}
                        <span className="text-xs text-gray-500 dark:text-gray-400 ml-1">
                          ({visit.status})
                        </span>
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {formatCallDate(visit.startDate)}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Open action items */}
          <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <span className="text-xl">✅</span>
                <h3 className="font-semibold text-gray-900 dark:text-white">Action Items</h3>
              </div>
              {openActionItems.totalCount > 0 && (
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {openActionItems.doneCount}/{openActionItems.totalCount} done
                </span>
              )}
            </div>
            {sourcesLoading ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">Loading…</p>
            ) : openActionItems.open.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {openActionItems.totalCount > 0
                  ? 'All recent action items are complete. 🎉'
                  : 'No action items from recent calls.'}
              </p>
            ) : (
              <ul className="space-y-2">
                {openActionItems.open.map((item, index) => (
                  <li
                    key={index}
                    className="flex items-start gap-2"
                    title={`${item.callTitle} · ${formatCallDate(item.callDate)}`}
                  >
                    <span className="shrink-0 text-sm leading-5">⬜</span>
                    <p className="text-sm text-gray-800 dark:text-gray-200 line-clamp-2 min-w-0">
                      {item.text}
                      {item.assigneeName && (
                        <span className="text-xs text-gray-500 dark:text-gray-400 ml-1">
                          ({item.assigneeName})
                        </span>
                      )}
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Other recent activity */}
          <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-xl">⚡</span>
              <h3 className="font-semibold text-gray-900 dark:text-white">Recent Activity</h3>
            </div>
            {sourcesLoading ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">Loading…</p>
            ) : recentActivityEvents.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                No other activity in the last 60 days.
              </p>
            ) : (
              <ul className="space-y-2.5">
                {recentActivityEvents.map((event) => (
                  <li key={event.id} className="flex items-start gap-2">
                    <span className="shrink-0 text-sm leading-5">
                      {{ task: '✅', checkin: '💓', visit: '📍' }[event.icon] || '⚡'}
                    </span>
                    <div className="min-w-0">
                      <p className="text-sm text-gray-800 dark:text-gray-200 line-clamp-2">
                        {event.label}
                        {event.department && (
                          <span className="ml-1">{getPortalEmoji(event.department)}</span>
                        )}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {event.detail ? `${event.detail} · ` : ''}
                        {formatRelativeDays(event.date)}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
